import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Chip, FormControl, InputLabel, MenuItem, Paper, Select, TextField, Tooltip, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import HistoryIcon from '@mui/icons-material/History';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountingDataTable, { DataTableColumn } from '../accounting/shared/AccountingDataTable';
import accessControlService, { AccessAgent, AccessPassLog } from '../../services/accessControlService';
import { formatTrDateTime } from './accessTime';

type Severity = 'success' | 'error' | 'info' | 'warning';

interface Props {
  /** Panel/kapı süzgeçleri Kapılar sekmesindeki envanterden beslenir. */
  agents: AccessAgent[];
  notify: (message: string, severity?: Severity) => void;
  cm: (key: string, defaultValue: string, options?: Record<string, unknown>) => string;
}

// Sunucu UTC yazıyor; sayfanın geri kalanıyla aynı GMT+3 gösterimi kullanılır.
const formatTimestamp = (value: string) => formatTrDateTime(value, { withSeconds: true, fallback: value });

/** `datetime-local` değeri sunucuya UTC olarak gönderilir. */
const toUtcParam = (localValue: string) => {
  if (!localValue) return undefined;
  const date = new Date(localValue);
  return isNaN(date.getTime()) ? undefined : date.toISOString();
};

const PassLogsTab: React.FC<Props> = ({ agents, notify, cm }) => {
  const [logs, setLogs] = useState<AccessPassLog[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(false);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [agentId, setAgentId] = useState<number | ''>('');
  const [doorFilter, setDoorFilter] = useState('');
  const [status, setStatus] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Kapı süzgeci `deviceKey:doorNumber` taşır: aynı kapı numarası farklı panellerde
  // tekrarlandığı için tek başına numara ayırt edici değil.
  const doorOptions = useMemo(() => {
    const options: { value: string; label: string }[] = [];
    agents
      .filter((agent) => agentId === '' || agent.id === agentId)
      .forEach((agent) => agent.panels.forEach((panel) => panel.doors.forEach((door) => {
        const panelLabel = panel.name || panel.deviceKey.slice(0, 8);
        const doorLabel = door.name || `${cm('accessManagement.doorNumber', 'Door')} ${door.doorNumber}`;
        options.push({
          value: `${panel.deviceKey}:${door.doorNumber}`,
          label: `${panelLabel} • ${doorLabel}`,
        });
      })));
    return options;
  }, [agents, agentId, cm]);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const [deviceKey, doorNumber] = doorFilter ? doorFilter.split(':') : [undefined, undefined];
      const res = await accessControlService.getPassLogs({
        page,
        pageSize,
        search: search || undefined,
        startDate: toUtcParam(startDate),
        endDate: toUtcParam(endDate),
        agentId: agentId === '' ? undefined : agentId,
        deviceKey: deviceKey || undefined,
        doorNumber: doorNumber ? Number(doorNumber) : undefined,
        status: status || undefined,
      });
      setLogs(res.data?.data || []);
      setTotalCount(res.data?.totalCount || 0);
    } catch (error: any) {
      setLogs([]);
      setTotalCount(0);
      notify(error?.response?.data?.message || cm('accessManagement.errors.logsLoadFailed', 'Pass logs could not be loaded.'), 'error');
    }
    setLoading(false);
  }, [page, pageSize, search, startDate, endDate, agentId, doorFilter, status, notify, cm]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  const resetFilters = () => {
    setSearchInput('');
    setStartDate('');
    setEndDate('');
    setAgentId('');
    setDoorFilter('');
    setStatus('');
    setPage(1);
  };

  const columns: DataTableColumn<AccessPassLog>[] = [
    {
      id: 'eventTimestamp',
      label: cm('accessManagement.passLogs.timestamp', 'Time'),
      width: '16%',
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="body2">{formatTimestamp(row.eventTimestamp)}</Typography>
          {row.decidedOffline && (
            <Tooltip title={cm('accessManagement.passLogs.offlineHint', 'Decided while the panel computer was offline.')}>
              <CloudOffIcon fontSize="small" sx={{ color: 'warning.main' }} />
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      id: 'card',
      label: cm('accessManagement.passLogs.card', 'Card'),
      width: '20%',
      render: (row) => (
        <Box>
          <Typography variant="body2">
            {row.cardLabel || cm('accessManagement.passLogs.unknownCard', 'Unknown card')}
          </Typography>
          <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
            {row.cardUID}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'door',
      label: cm('accessManagement.passLogs.door', 'Door'),
      width: '20%',
      render: (row) => (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {row.direction === 'entry' && <LoginIcon fontSize="small" color="success" />}
            {row.direction === 'exit' && <LogoutIcon fontSize="small" color="warning" />}
            <Typography variant="body2">
              {row.doorName || `${cm('accessManagement.doorNumber', 'Door')} ${row.doorNumber}`}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
            {row.panelDeviceKey.slice(0, 8)}:{row.doorNumber}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'status',
      label: cm('accessManagement.passLogs.status', 'Result'),
      width: '12%',
      align: 'center',
      render: (row) => (row.status === 'ALLOWED'
        ? <Chip size="small" color="success" label={cm('accessManagement.passLogs.allowed', 'Allowed')} />
        : <Chip size="small" color="error" variant="outlined" label={cm('accessManagement.passLogs.denied', 'Denied')} />),
    },
    {
      id: 'denialReason',
      label: cm('accessManagement.passLogs.reason', 'Reason'),
      width: '32%',
      render: (row) => (row.denialReason
        ? <Typography variant="body2" color="text.secondary">{row.denialReason}</Typography>
        : <Typography variant="caption" color="text.disabled">—</Typography>),
    },
  ];

  return (
    <>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              {cm('accessManagement.passLogs.title', 'Pass Logs')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {cm('accessManagement.passLogs.hint', 'Both allowed and denied passes are recorded. Logs collected while offline arrive when the connection is restored.')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" onClick={resetFilters}>
              {cm('accessManagement.passLogs.clearFilters', 'Clear filters')}
            </Button>
            <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={loadLogs}>
              {cm('common.refresh', 'Refresh')}
            </Button>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            sx={{ minWidth: 240 }}
            placeholder={cm('accessManagement.passLogs.search', 'Search card, account or door')}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} /> }}
          />
          <TextField
            size="small"
            type="datetime-local"
            label={cm('accessManagement.passLogs.startDate', 'From')}
            value={startDate}
            onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            type="datetime-local"
            label={cm('accessManagement.passLogs.endDate', 'To')}
            value={endDate}
            onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
            InputLabelProps={{ shrink: true }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>{cm('accessManagement.passLogs.agent', 'Panel computer')}</InputLabel>
            <Select
              value={agentId}
              label={cm('accessManagement.passLogs.agent', 'Panel computer')}
              onChange={(e) => {
                setAgentId(e.target.value === '' ? '' : Number(e.target.value));
                setDoorFilter('');
                setPage(1);
              }}
            >
              <MenuItem value="">
                <em>{cm('accessManagement.passLogs.all', 'All')}</em>
              </MenuItem>
              {agents.map((agent) => (
                <MenuItem key={agent.id} value={agent.id}>
                  {agent.name || agent.screenCode}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>{cm('accessManagement.passLogs.door', 'Door')}</InputLabel>
            <Select
              value={doorFilter}
              label={cm('accessManagement.passLogs.door', 'Door')}
              onChange={(e) => { setDoorFilter(String(e.target.value)); setPage(1); }}
            >
              <MenuItem value="">
                <em>{cm('accessManagement.passLogs.all', 'All')}</em>
              </MenuItem>
              {doorOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{cm('accessManagement.passLogs.status', 'Result')}</InputLabel>
            <Select
              value={status}
              label={cm('accessManagement.passLogs.status', 'Result')}
              onChange={(e) => { setStatus(String(e.target.value)); setPage(1); }}
            >
              <MenuItem value="">
                <em>{cm('accessManagement.passLogs.all', 'All')}</em>
              </MenuItem>
              <MenuItem value="ALLOWED">{cm('accessManagement.passLogs.allowed', 'Allowed')}</MenuItem>
              <MenuItem value="DENIED">{cm('accessManagement.passLogs.denied', 'Denied')}</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <AccountingDataTable<AccessPassLog>
        columns={columns}
        rows={logs}
        getRowId={(row) => String(row.id)}
        loading={loading}
        serverSidePagination
        totalCount={totalCount}
        page={page - 1}
        pageSize={pageSize}
        onPageChange={(nextPage, nextPageSize) => {
          setPage(nextPage + 1);
          setPageSize(nextPageSize);
        }}
        emptyMessage={cm('accessManagement.passLogs.empty', 'No pass log found.')}
        emptyIcon={<HistoryIcon sx={{ fontSize: 42, color: 'text.disabled' }} />}
      />
    </>
  );
};

export default PassLogsTab;
